import { makeAutoObservable } from 'mobx'
import CalendarEventService from './../../../shared/services/CalendarEvent.service'
import moment from 'moment'

/**
 * USABG Holiday calendar ID from the database.
 * @constant {number}
 */
const USABG_HOLIDAY_CALENDAR_ID = 20

/**
 * HolidayStore
 *
 * MobX store for managing USABG holiday data.
 * Fetches holiday events from the calendar_events table and provides
 * them in a format suitable for year-view calendar display.
 *
 * @example
 * import HolidayStore from './store/Holiday.store'
 *
 * useEffect(() => {
 *   HolidayStore.init()
 *   return () => HolidayStore.reset()
 * }, [])
 */
class HolidayStore {
  /**
   * Loading state indicator.
   * True when initial data is being loaded.
   * @type {boolean}
   */
  isLoading = false

  /**
   * Fetching state indicator.
   * True when API request is in progress.
   * @type {boolean}
   */
  isFetching = false

  /**
   * Error state.
   * Contains error message if fetch fails.
   * @type {string|null}
   */
  error = null

  /**
   * The currently selected year for display.
   * @type {number}
   */
  selectedYear = new Date().getFullYear()

  /**
   * Array of holiday events fetched from the API.
   * Each event contains: id, eventId, calendarId, summary,
   * description, startsAt, endsAt, timezone, date, displayDate, dayOfWeek
   * @type {Array<Object>}
   */
  holidays = []

  /**
   * Map of holidays grouped by date (YYYY-MM-DD format).
   * Computed from holidays array for efficient lookup.
   * @type {Object<string, Array<Object>>}
   */
  holidaysByDate = {}

  constructor() {
    makeAutoObservable(this)
  }

  /**
   * Initialize the store by fetching holidays for the current year.
   * Called on component mount.
   *
   * @returns {Promise<void>}
   */
  init = async () => {
    this.isLoading = true
    await this.fetchHolidaysForYear(this.selectedYear)
    this.isLoading = false
  }

  /**
   * Set the selected year and fetch holidays for that year.
   *
   * @param {number} year - The year to fetch holidays for
   * @returns {Promise<void>}
   */
  setYear = async year => {
    if (year !== this.selectedYear) {
      this.selectedYear = year
      await this.fetchHolidaysForYear(year)
    }
  }

  /**
   * Fetch holidays for a specific year from the calendar_events API.
   * Uses the getByCalendarId endpoint to fetch directly by calendar ID,
   * bypassing usertype permission filtering.
   *
   * @param {number} year - The year to fetch holidays for
   * @returns {Promise<void>}
   */
  fetchHolidaysForYear = async year => {
    this.isFetching = true
    this.error = null

    const startDate = `${year}-01-01`
    const endDate = `${year}-12-31`

    try {
      // Use getByCalendarId to fetch holidays directly by calendar ID
      // This bypasses usertype permission filtering
      const holidayEvents =
        (await CalendarEventService.getByCalendarId(USABG_HOLIDAY_CALENDAR_ID, startDate, endDate)) || []

      this.holidays = holidayEvents.map(event => {
        // Parse as UTC to prevent timezone shifting for all-day events
        // Database stores dates like "2026-01-26 00:00:00" which should stay on that date
        const eventDate = moment.utc(event.starts_at)

        return {
          id: event.id,
          eventId: event.event_id,
          calendarId: event.calendar_id,
          summary: event.summary,
          description: event.description,
          startsAt: event.starts_at,
          endsAt: event.ends_at,
          timezone: event.tz,
          // Derived fields for display - use UTC to preserve the date
          date: eventDate.format('YYYY-MM-DD'),
          displayDate: eventDate.format('MMM D, YYYY'),
          dayOfWeek: eventDate.format('dddd'),
        }
      })

      // Sort holidays chronologically
      this.holidays.sort((a, b) => moment(a.startsAt).diff(moment(b.startsAt)))

      // Build lookup map by date
      this._buildHolidaysByDateMap()
    } catch (ex) {
      console.error('Failed to fetch holidays:', ex)
      this.error = 'Failed to load holidays. Please try again.'
      this.holidays = []
      this.holidaysByDate = {}
    } finally {
      this.isFetching = false
    }
  }

  /**
   * Build a map of holidays grouped by date for efficient lookup.
   * @private
   */
  _buildHolidaysByDateMap = () => {
    this.holidaysByDate = {}

    this.holidays.forEach(holiday => {
      const dateKey = holiday.date
      if (!this.holidaysByDate[dateKey]) {
        this.holidaysByDate[dateKey] = []
      }
      this.holidaysByDate[dateKey].push(holiday)
    })
  }

  /**
   * Check if a specific date has holidays.
   *
   * @param {string} dateStr - Date string in YYYY-MM-DD format
   * @returns {boolean}
   */
  hasHoliday = dateStr => {
    return !!this.holidaysByDate[dateStr]
  }

  /**
   * Get holidays for a specific date.
   *
   * @param {string} dateStr - Date string in YYYY-MM-DD format
   * @returns {Array<Object>} Array of holiday objects for the date
   */
  getHolidaysForDate = dateStr => {
    return this.holidaysByDate[dateStr] || []
  }

  /**
   * Get all holidays for a specific month.
   *
   * @param {number} month - Month number (0-11)
   * @returns {Array<Object>} Array of holidays in the month
   */
  getHolidaysForMonth = month => {
    return this.holidays.filter(holiday => {
      const holidayMonth = moment(holiday.startsAt).month()
      return holidayMonth === month
    })
  }

  /**
   * Reset the store to initial state.
   */
  reset = () => {
    this.isLoading = false
    this.isFetching = false
    this.error = null
    this.selectedYear = new Date().getFullYear()
    this.holidays = []
    this.holidaysByDate = {}
  }
}

export default new HolidayStore()
